/**
 * Fret-interval recognition — guitar rung 1 (shape-family redesign, PR-B).
 *
 * The exercise is: hear the root ("do"), then an interval above it — tap the
 * upper note on the partner string of the designated string pair.
 *
 * The answer space is constrained to the partner string only (`allowedStrings`
 * = [pair.upper]). This forces the user to learn the shape as a fixed fret
 * offset rather than hunting across the whole neck.
 *
 * Three shape families (new, PR-B):
 *   "standard"  — any of the four P4 pairs (E–A, A–D, D–G, B–e); one FSRS
 *                 item per interval, generator randomises the pair + fret.
 *   "gb-offset" — the G–B pair only; same 7 shapes but +1 fret each.
 *   "octave"    — string-skip P8 shapes; 3 canonical grips (E–D, A–G, D–B).
 *
 * Items without a shapeFamily param (retired `fret-intervals-octaves` node)
 * fall through to the legacy whole-neck generator for backward compatibility —
 * those cards are no longer scheduled but may still appear in old sessions.
 *
 * Pure: builds an `Exercise` whose `meta.fretboard` carries the answer
 * contract. Graded by `gradeFretboard`, not the option-button grader.
 *
 * See docs/design/guitar-harmony-v2.md §4.1 / §6.
 */

import { AudioPlanBuilder } from "./audio-plan.js";
import { Rng, type Exercise, type FretboardPrompt } from "./exercise.js";
import { appendCadence, pickKey, type CadenceOptions, type KeyChoice } from "./utils.js";
import { buildIntervalTeachPlan } from "./generators/interval.js";
import {
  STANDARD_TUNING,
  P4_STRING_PAIRS,
  GB_STRING_PAIR,
  OCTAVE_SHAPES,
  WIDE_OCTAVE_SHAPES,
  SKIP1_INTERVAL_SHAPES,
  positionsForMidi,
  midiAt,
  type FretPosition,
} from "@music-app/theory-engine";

// ─── Constants ────────────────────────────────────────────────────────────────

/** Semitones above the root for each interval symbol. */
const INTERVAL_SEMITONES: Record<string, number> = {
  m2: 1, M2: 2, m3: 3, M3: 4, P4: 5, P5: 7, m6: 8, M6: 9, P8: 12,
};

const INTERVAL_NAMES: Record<string, string> = {
  m2: "Minor 2nd", M2: "Major 2nd",
  m3: "Minor 3rd", M3: "Major 3rd",
  P4: "Perfect 4th", P5: "Perfect 5th",
  m6: "Minor 6th", M6: "Major 6th",
  P8: "Octave",
};

/** Pitch-class → display name, using flats for accidentals. */
const PC_NAMES = ["C","D♭","D","E♭","E","F","F♯","G","A♭","A","B♭","B"] as const;

// ─── Param types ─────────────────────────────────────────────────────────────

export type ShapeFamily = "standard" | "gb-offset" | "octave" | "wide-octave" | "skip1-interval";

export interface FretIntervalParams {
  interval: string;
  /** Which shape-family context to generate. Absent → legacy whole-neck mode. */
  shapeFamily?: ShapeFamily;
  /** For "octave" family — identifies which shape.
   *  "oct-ed" → E–D (6–4), "oct-ag" → A–G (5–3), "oct-db" → D–B (4–2), "oct-ge" → G–e (3–1). */
  octaveShapeSymbol?: string;
  /** For "wide-octave" family — identifies which skip-2-string grip.
   *  "wide-eg" → E–G (6–3), "wide-ab" → A–B (5–2), "wide-de" → D–e (4–1). */
  wideOctaveShapeSymbol?: string;
  /** For "skip1-interval" family — identifies which same-fret skip-1 shape.
   *  "skip1-m7-ed", "skip1-m7-ag", "skip1-m6-db", "skip1-m6-ge". */
  skip1IntervalSymbol?: string;
}

// ─── Key-choice helper ────────────────────────────────────────────────────────

/**
 * Build a KeyChoice for the cadence based on a root pitch class.
 * The cadence tonic is placed in octave 4 (60–71) so appendCadence's
 * voice-leading stays within the [48, 76] working range.
 */
function keyChoiceForPc(pc: number): KeyChoice {
  const pcName = PC_NAMES[pc]!;
  const hasFlat  = pcName.includes("♭");
  const hasSharp = pcName.includes("♯");
  const letter     = pcName.replace(/[♭♯]/u, "");
  const accidental: -1 | 0 | 1 = hasFlat ? -1 : hasSharp ? 1 : 0;
  return {
    letter,
    accidental,
    mode: "ionian",
    tonicMidi: 60 + pc, // always octave 4 (C4–B4 = 60–71), safe for voice-leading
    name: `${pcName} major`,
  };
}

// ─── Fret-range helpers ───────────────────────────────────────────────────────

/**
 * Valid root-fret range so both root and upper fret stay within [1, fretCount − 1].
 * For a P4 pair: upperFret = rootFret + semitones − 5.
 * For the G–B pair: upperFret = rootFret + semitones − 4.
 */
function rootFretRange(
  semitones: number,
  family: "standard" | "gb-offset",
): { min: number; max: number } {
  const tuningDiff = family === "gb-offset" ? 4 : 5;
  const offset = semitones - tuningDiff; // upperFret = rootFret + offset
  const limit  = STANDARD_TUNING.fretCount - 1;
  const minRoot = Math.max(2, 1 - offset);
  const maxRoot = Math.min(limit, limit - offset);
  // Narrow to a mid-neck zone (2–11) so shapes feel grounded.
  return { min: Math.max(minRoot, 2), max: Math.min(maxRoot, 11) };
}

/** Narrow listen-mode window containing both root and upper fret ± 1. */
function listenWindow(rootFret: number, upperFret: number): { from: number; to: number } {
  const lo = Math.min(rootFret, upperFret);
  const hi = Math.max(rootFret, upperFret);
  return {
    from: Math.max(0, lo - 1),
    to: Math.min(STANDARD_TUNING.fretCount, hi + 1),
  };
}

// ─── Public generator ─────────────────────────────────────────────────────────

export function generateFretInterval(
  itemId: string,
  params: FretIntervalParams,
  seed: number,
  cadenceOpts?: CadenceOptions,
): Exercise {
  // Items without shapeFamily are from the retired fret-intervals-octaves node.
  // Use the legacy whole-neck generator so old sessions don't crash.
  if (params.shapeFamily === undefined) {
    return generateLegacyWholeNeck(itemId, params, seed, cadenceOpts);
  }

  const rng = new Rng(seed);

  if (params.shapeFamily === "octave") {
    return generateOctaveShape(itemId, params, seed, rng, cadenceOpts);
  }

  if (params.shapeFamily === "wide-octave") {
    return generateWideOctaveShape(itemId, params, seed, rng, cadenceOpts);
  }

  if (params.shapeFamily === "skip1-interval") {
    return generateSkip1IntervalShape(itemId, params, seed, rng, cadenceOpts);
  }

  return generatePairShape(itemId, params, seed, rng, cadenceOpts);
}

// ─── Pair-constrained generator (standard & gb-offset) ───────────────────────

function generatePairShape(
  itemId: string,
  params: FretIntervalParams,
  seed: number,
  rng: Rng,
  cadenceOpts?: CadenceOptions,
): Exercise {
  const family = params.shapeFamily as "standard" | "gb-offset";
  const semitones = INTERVAL_SEMITONES[params.interval];
  if (semitones === undefined) {
    throw new Error(`Unknown fret interval: ${params.interval}`);
  }

  const pair = family === "gb-offset"
    ? GB_STRING_PAIR
    : P4_STRING_PAIRS[rng.int(P4_STRING_PAIRS.length)]!;

  const { min, max } = rootFretRange(semitones, family);
  if (min > max) {
    throw new Error(`No valid root fret range for ${params.interval} on ${family}`);
  }
  const rootFret   = min + rng.int(max - min + 1);
  const tuningDiff = family === "gb-offset" ? 4 : 5;
  const upperFret  = rootFret + semitones - tuningDiff;

  const rootPos:  FretPosition = { stringIndex: pair.lower, fret: rootFret };
  const upperPos: FretPosition = { stringIndex: pair.upper, fret: upperFret };

  const rootMidi   = midiAt(STANDARD_TUNING, rootPos);
  const targetMidi = midiAt(STANDARD_TUNING, upperPos);

  const fretboard: FretboardPrompt = {
    tuning: "standard",
    given: [rootPos],
    notesToPlace: 1,
    allowedStrings: [pair.upper],
    acceptedPositions: [upperPos],
    solution: [upperPos],
    listenFretWindow: listenWindow(rootFret, upperFret),
  };

  const rootPc = ((rootMidi % 12) + 12) % 12;
  const key    = keyChoiceForPc(rootPc);

  const builder = new AudioPlanBuilder();
  appendCadence(builder, key, cadenceOpts);
  builder.addNote(rootMidi, 0.9, 0.2, "target");
  builder.advance(0.95);
  builder.addNote(targetMidi, 1.3, 0.22, "target");
  builder.advance(1.4);
  const audio = builder.build();

  const ivName = INTERVAL_NAMES[params.interval] ?? params.interval;
  const explanation = family === "gb-offset"
    ? `${ivName} (${params.interval}) on G–B — +1 fret vs. standard shape.`
    : `${ivName} (${params.interval}) — same shape across all P4 pairs.`;

  return {
    itemId,
    exerciseType: "fret-interval-recognition",
    audio,
    prompt: "Tap the note you heard above the root.",
    options: [],
    correctAnswerId: "",
    meta: {
      keyName: key.name,
      seed,
      explanation,
      distractorCount: 0,
      tonicMidi: rootMidi,
      tonalityMode: "major",
      teachAudio: buildIntervalTeachPlan(rootMidi, targetMidi),
      fretboard,
    },
  };
}

// ─── Octave-shape generator ───────────────────────────────────────────────────

const OCTAVE_SHAPE_SYMBOL_INDEX: Record<string, number> = {
  "oct-ed": 0,
  "oct-ag": 1,
  "oct-db": 2,
  "oct-ge": 3,
};

const WIDE_OCTAVE_SYMBOL_INDEX: Record<string, number> = {
  "wide-eg": 0,
  "wide-ab": 1,
  "wide-de": 2,
};

function generateOctaveShape(
  itemId: string,
  params: FretIntervalParams,
  seed: number,
  rng: Rng,
  cadenceOpts?: CadenceOptions,
): Exercise {
  const shapeIdx = params.octaveShapeSymbol !== undefined
    ? (OCTAVE_SHAPE_SYMBOL_INDEX[params.octaveShapeSymbol] ?? 0)
    : rng.int(OCTAVE_SHAPES.length);
  const shape = OCTAVE_SHAPES[shapeIdx]!;

  const maxRoot = Math.min(10, STANDARD_TUNING.fretCount - shape.fretOffset - 1);
  const rootFret  = 2 + rng.int(maxRoot - 2 + 1);
  const upperFret = rootFret + shape.fretOffset;

  const rootPos:  FretPosition = { stringIndex: shape.lower, fret: rootFret };
  const upperPos: FretPosition = { stringIndex: shape.upper, fret: upperFret };

  const rootMidi   = midiAt(STANDARD_TUNING, rootPos);
  const targetMidi = midiAt(STANDARD_TUNING, upperPos);

  const fretboard: FretboardPrompt = {
    tuning: "standard",
    given: [rootPos],
    notesToPlace: 1,
    allowedStrings: [shape.upper],
    acceptedPositions: [upperPos],
    solution: [upperPos],
    listenFretWindow: listenWindow(rootFret, upperFret),
  };

  const rootPc = ((rootMidi % 12) + 12) % 12;
  const key    = keyChoiceForPc(rootPc);

  const builder = new AudioPlanBuilder();
  appendCadence(builder, key, cadenceOpts);
  builder.addNote(rootMidi, 0.9, 0.2, "target");
  builder.advance(0.95);
  builder.addNote(targetMidi, 1.3, 0.22, "target");
  builder.advance(1.4);
  const audio = builder.build();

  return {
    itemId,
    exerciseType: "fret-interval-recognition",
    audio,
    prompt: "Tap the octave above the root.",
    options: [],
    correctAnswerId: "",
    meta: {
      keyName: key.name,
      seed,
      explanation: `Octave — ${shape.label} grip (+${shape.fretOffset} frets from root).`,
      distractorCount: 0,
      tonicMidi: rootMidi,
      tonalityMode: "major",
      teachAudio: buildIntervalTeachPlan(rootMidi, targetMidi),
      fretboard,
    },
  };
}

// ─── Skip-1-interval generator (same-fret m7/M6 shapes) ─────────────────────

const SKIP1_INTERVAL_SYMBOL_INDEX: Record<string, number> = {
  "skip1-m7-ed": 0,
  "skip1-m7-ag": 1,
  "skip1-m6-db": 2,
  "skip1-m6-ge": 3,
};

const SKIP1_INTERVAL_NAMES: Record<string, string> = {
  m7: "Minor 7th",
  M6: "Major 6th",
};

function generateSkip1IntervalShape(
  itemId: string,
  params: FretIntervalParams,
  seed: number,
  rng: Rng,
  cadenceOpts?: CadenceOptions,
): Exercise {
  const shapeIdx = params.skip1IntervalSymbol !== undefined
    ? (SKIP1_INTERVAL_SYMBOL_INDEX[params.skip1IntervalSymbol] ?? 0)
    : rng.int(SKIP1_INTERVAL_SHAPES.length);
  const shape = SKIP1_INTERVAL_SHAPES[shapeIdx]!;

  const rootFret  = 2 + rng.int(9); // 2–10, upperFret = rootFret + 0 always in range
  const upperFret = rootFret + shape.fretOffset;

  const rootPos:  FretPosition = { stringIndex: shape.lower, fret: rootFret };
  const upperPos: FretPosition = { stringIndex: shape.upper, fret: upperFret };

  const rootMidi   = midiAt(STANDARD_TUNING, rootPos);
  const targetMidi = midiAt(STANDARD_TUNING, upperPos);

  const fretboard: FretboardPrompt = {
    tuning: "standard",
    given: [rootPos],
    notesToPlace: 1,
    allowedStrings: [shape.upper],
    acceptedPositions: [upperPos],
    solution: [upperPos],
    listenFretWindow: { from: Math.max(0, rootFret - 1), to: rootFret + 1 },
  };

  const rootPc = ((rootMidi % 12) + 12) % 12;
  const key    = keyChoiceForPc(rootPc);

  const builder = new AudioPlanBuilder();
  appendCadence(builder, key, cadenceOpts);
  builder.addNote(rootMidi, 0.9, 0.2, "target");
  builder.advance(0.95);
  builder.addNote(targetMidi, 1.3, 0.22, "target");
  builder.advance(1.4);
  const audio = builder.build();

  const ivName = SKIP1_INTERVAL_NAMES[shape.interval] ?? shape.interval;

  return {
    itemId,
    exerciseType: "fret-interval-recognition",
    audio,
    prompt: "Tap the note you heard above the root.",
    options: [],
    correctAnswerId: "",
    meta: {
      keyName: key.name,
      seed,
      explanation: `${ivName} (${shape.interval}) — ${shape.label}. Same fret as the root.`,
      distractorCount: 0,
      tonicMidi: rootMidi,
      tonalityMode: "major",
      teachAudio: buildIntervalTeachPlan(rootMidi, targetMidi),
      fretboard,
    },
  };
}

// ─── Wide-octave generator (skip-2-string grips) ─────────────────────────────

function generateWideOctaveShape(
  itemId: string,
  params: FretIntervalParams,
  seed: number,
  rng: Rng,
  cadenceOpts?: CadenceOptions,
): Exercise {
  const shapeIdx = params.wideOctaveShapeSymbol !== undefined
    ? (WIDE_OCTAVE_SYMBOL_INDEX[params.wideOctaveShapeSymbol] ?? 0)
    : rng.int(WIDE_OCTAVE_SHAPES.length);
  const shape = WIDE_OCTAVE_SHAPES[shapeIdx]!;

  // fretOffset is negative here: upperFret = rootFret + fretOffset.
  // rootFret must be >= abs(fretOffset) to keep upperFret >= 0.
  const minRoot = Math.max(2, -shape.fretOffset);
  const maxRoot = Math.min(10, STANDARD_TUNING.fretCount - 1);
  const rootFret  = minRoot + rng.int(maxRoot - minRoot + 1);
  const upperFret = rootFret + shape.fretOffset;

  const rootPos:  FretPosition = { stringIndex: shape.lower, fret: rootFret };
  const upperPos: FretPosition = { stringIndex: shape.upper, fret: upperFret };

  const rootMidi   = midiAt(STANDARD_TUNING, rootPos);
  const targetMidi = midiAt(STANDARD_TUNING, upperPos);

  const fretboard: FretboardPrompt = {
    tuning: "standard",
    given: [rootPos],
    notesToPlace: 1,
    allowedStrings: [shape.upper],
    acceptedPositions: [upperPos],
    solution: [upperPos],
    listenFretWindow: listenWindow(rootFret, upperFret),
  };

  const rootPc = ((rootMidi % 12) + 12) % 12;
  const key    = keyChoiceForPc(rootPc);

  const builder = new AudioPlanBuilder();
  appendCadence(builder, key, cadenceOpts);
  builder.addNote(rootMidi, 0.9, 0.2, "target");
  builder.advance(0.95);
  builder.addNote(targetMidi, 1.3, 0.22, "target");
  builder.advance(1.4);
  const audio = builder.build();

  return {
    itemId,
    exerciseType: "fret-interval-recognition",
    audio,
    prompt: "Tap the octave above the root.",
    options: [],
    correctAnswerId: "",
    meta: {
      keyName: key.name,
      seed,
      explanation: `Octave — ${shape.label} wide grip (${shape.fretOffset} frets from root).`,
      distractorCount: 0,
      tonicMidi: rootMidi,
      tonalityMode: "major",
      teachAudio: buildIntervalTeachPlan(rootMidi, targetMidi),
      fretboard,
    },
  };
}

// ─── Legacy whole-neck generator (retired fret-intervals-octaves node) ────────

/**
 * Original whole-neck generator: root at any position, accepted = all neck
 * positions of the target MIDI. Kept for backward compatibility — items from
 * the retired `fret-intervals-octaves` node still land here if encountered.
 */
function generateLegacyWholeNeck(
  itemId: string,
  params: FretIntervalParams,
  seed: number,
  cadenceOpts?: CadenceOptions,
): Exercise {
  const rng = new Rng(seed);
  const semitones = INTERVAL_SEMITONES[params.interval];
  if (semitones === undefined) {
    throw new Error(`Unknown fret interval: ${params.interval}`);
  }

  const key = pickKey(rng, "major");
  const rootMidi   = key.tonicMidi - 12;
  const targetMidi = rootMidi + semitones;

  const acceptedPositions = positionsForMidi(STANDARD_TUNING, targetMidi);

  const canonicalPos = (midi: number): FretPosition | null => {
    const positions = positionsForMidi(STANDARD_TUNING, midi);
    if (positions.length === 0) return null;
    return positions.reduce((best, p) => (p.fret < best.fret ? p : best));
  };

  const rootPos    = canonicalPos(rootMidi);
  const solutionPos = canonicalPos(targetMidi);

  const fretboard: FretboardPrompt = {
    tuning: "standard",
    given: rootPos ? [rootPos] : [],
    notesToPlace: 1,
    acceptedPositions,
    solution: solutionPos ? [solutionPos] : [],
  };

  const builder = new AudioPlanBuilder();
  appendCadence(builder, key, cadenceOpts);
  builder.addNote(rootMidi, 0.9, 0.2, "target");
  builder.advance(0.95);
  builder.addNote(targetMidi, 1.3, 0.22, "target");
  builder.advance(1.4);
  const audio = builder.build();

  return {
    itemId,
    exerciseType: "fret-interval-recognition",
    audio,
    prompt: "Tap the note you heard above the root.",
    options: [],
    correctAnswerId: "",
    meta: {
      keyName: key.name,
      seed,
      explanation: `${INTERVAL_NAMES[params.interval] ?? params.interval} (${params.interval}) above the root.`,
      distractorCount: 0,
      tonicMidi: key.tonicMidi,
      tonalityMode: "major",
      teachAudio: buildIntervalTeachPlan(rootMidi, targetMidi),
      fretboard,
    },
  };
}
